/**
 * ReferralBlock (#1018) — the referral "slow path" on /apply.
 *
 * A referral to TRF is a human act: a hospital social worker tells a family
 * about the program. An application code was only ever a shortcut for
 * DOCUMENTING that act. This block is the equally-valid slow path — a family
 * with no code names their hospital and the person who told them about TRF,
 * and their application is attributed just as well.
 *
 * NO FIELD HERE IS `required`. A mandatory origin field makes honest people
 * "type something random to proceed" — you get worse data, not better, plus
 * a family stopped at a door that was supposed to be open. An optional field
 * yields fewer answers, but every answer it does yield is one a family chose
 * to give. Do not add `required` to any input in this file.
 *
 * Wire contract: `POST /api/applications` accepts, by explicit key,
 * `referral_hospital_id`, `referral_hospital_other`, `referral_contact_name`
 * — those exact names are pinned by the backend tests. This component's
 * local shape (`ReferralValue`) maps onto them 1:1 at the call site that
 * consumes `onChange`.
 *
 * The hospital picker is a debounced typeahead over
 * `api.partnerHospitals.search` (GET /api/partner-hospitals?q=, public,
 * rate-limited, capped at 20 — #1018 backend). Selecting a suggestion links
 * the application to a real PartnerHospital record (`hospital_id`), which is
 * what makes a per-cycle, per-partner report possible later (#1019).
 *
 * "My hospital isn't listed" is the escape hatch the whole slow path exists
 * for: without it, a family whose hospital is missing from the list is stuck
 * at a dead end, which is the exact failure this design is meant to avoid.
 * Choosing it reveals a free-text field (`hospital_other`) instead. The two
 * are mutually exclusive — selecting a suggestion clears `hospital_other`,
 * and choosing the escape hatch clears `hospital_id` — so a submission can
 * never carry both.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { FieldLabel, MonoLabel } from '@/components/editorial';
import { api } from '@/services/api';
import type { PartnerHospitalSummary } from '@/services/api';
import { useTranslation } from 'react-i18next';

export interface ReferralValue {
  hospital_id: string | null;
  hospital_other: string;
  contact_name: string;
}

const EMPTY_VALUE: ReferralValue = {
  hospital_id: null,
  hospital_other: '',
  contact_name: '',
};

/** Below this many characters, don't bother the network — every keystroke
 *  under 2 chars would match too much to be useful anyway. */
const MIN_QUERY_LENGTH = 2;

/** Debounce window for the typeahead — long enough that a normal typing
 *  cadence fires one request per pause, not one per keystroke. */
const DEBOUNCE_MS = 300;

const inputClass =
  'w-full rounded-sharp border border-stevens-indigo/25 bg-stevens-surface px-4 py-3 text-body text-stevens-ink placeholder:text-stevens-ink-placeholder transition-colors focus:outline-none focus:border-stevens-indigo';

function fieldLabel(htmlFor: string, children: string) {
  return (
    <FieldLabel htmlFor={htmlFor}>{children}</FieldLabel>
  );
}

export function ReferralBlock({ onChange }: { onChange: (value: ReferralValue) => void }) {
  const { t } = useTranslation('forms');
  const [value, setValue] = useState<ReferralValue>(EMPTY_VALUE);
  const [mode, setMode] = useState<'search' | 'other'>('search');

  // Typeahead state.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PartnerHospitalSummary[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  // The name of the currently-SELECTED hospital, if any — lets the fetch
  // effect tell "query just got set by a selection" apart from "the family
  // is typing", so picking a result doesn't immediately re-search itself.
  const selectedNameRef = useRef<string | null>(null);

  function commit(next: ReferralValue) {
    setValue(next);
    onChange(next);
  }

  // Debounced typeahead fetch.
  useEffect(() => {
    if (mode !== 'search') return undefined;
    const term = query.trim();
    if (term.length < MIN_QUERY_LENGTH || term === selectedNameRef.current) {
      setResults([]);
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(() => {
      api.partnerHospitals
        .search(term)
        .then((res) => {
          if (cancelled) return;
          setResults(res.data ?? []);
          setOpen(true);
          setActive(0);
        })
        .catch(() => {
          if (cancelled) return;
          setResults([]);
          setOpen(false);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, mode]);

  function onSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setQuery(next);
    selectedNameRef.current = null;
    if (value.hospital_id !== null) {
      commit({ ...value, hospital_id: null });
    }
  }

  function selectHospital(hit: PartnerHospitalSummary) {
    selectedNameRef.current = hit.name;
    setQuery(hit.name);
    setOpen(false);
    setResults([]);
    commit({ ...value, hospital_id: hit.id, hospital_other: '' });
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = results[active];
      if (hit) selectHospital(hit);
    }
  }

  function switchToOther() {
    setMode('other');
    setOpen(false);
    setResults([]);
    commit({ ...value, hospital_id: null });
  }

  function switchToSearch() {
    setMode('search');
    setQuery('');
    selectedNameRef.current = null;
    commit({ ...value, hospital_other: '' });
  }

  const activeHit = open ? results[active] : undefined;

  return (
    <div className="mb-10 max-w-[420px] flex flex-col gap-5">
      <div>
        <MonoLabel>{t('referral.eyebrow')}</MonoLabel>
        <p className="mt-2 text-body leading-snug text-stevens-ink-2">
          {t('referral.optional')}
        </p>
      </div>

      {mode === 'search' ? (
        <div>
          {fieldLabel('referral-hospital-search', t('referral.hospitalLabel'))}
          <div className="relative mt-2">
            <input
              id="referral-hospital-search"
              role="combobox"
              aria-expanded={open}
              aria-controls="referral-hospital-listbox"
              aria-autocomplete="list"
              aria-activedescendant={activeHit ? `referral-hospital-option-${activeHit.id}` : undefined}
              autoComplete="off"
              value={query}
              onChange={onSearchChange}
              onKeyDown={onSearchKeyDown}
              placeholder={t('referral.hospitalPlaceholder')}
              className={inputClass}
            />
            {loading && (
              <span className="mt-2 block text-body-sm text-stevens-ink-3">{t('referral.searching')}</span>
            )}
            {open && results.length > 0 && (
              <ul
                id="referral-hospital-listbox"
                role="listbox"
                aria-label={t('referral.matches')}
                className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-sharp border border-stevens-indigo/25 bg-stevens-surface"
              >
                {results.map((hit, i) => (
                  <li
                    key={hit.id}
                    id={`referral-hospital-option-${hit.id}`}
                    role="option"
                    aria-selected={i === active}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => selectHospital(hit)}
                    className={`cursor-pointer border-b border-stevens-indigo/10 px-4 py-2.5 last:border-b-0 text-body text-stevens-ink ${
                      i === active ? 'bg-stevens-indigo/10' : ''
                    }`}
                  >
                    {hit.name}
                    {hit.city ? (
                      <span className="ml-1 text-body-sm text-stevens-ink-3">
                        — {hit.city}, {hit.state}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={switchToOther}
            className="mt-2 text-body-sm text-stevens-indigo underline decoration-stevens-indigo/40 underline-offset-4 transition-colors hover:text-stevens-indigo-solid"
          >
            {t('referral.notListed')}
          </button>
        </div>
      ) : (
        <div>
          {fieldLabel('referral-hospital-other', t('referral.otherLabel'))}
          <input
            id="referral-hospital-other"
            value={value.hospital_other}
            onChange={(e) => commit({ ...value, hospital_id: null, hospital_other: e.target.value })}
            placeholder={t('referral.otherPlaceholder')}
            autoComplete="off"
            className="mt-2 w-full rounded-sharp border border-stevens-indigo/25 bg-stevens-surface px-4 py-3 text-body text-stevens-ink placeholder:text-stevens-ink-placeholder transition-colors focus:outline-none focus:border-stevens-indigo"
          />
          <button
            type="button"
            onClick={switchToSearch}
            className="mt-2 text-body-sm text-stevens-indigo underline decoration-stevens-indigo/40 underline-offset-4 transition-colors hover:text-stevens-indigo-solid"
          >
            {t('referral.searchInstead')}
          </button>
        </div>
      )}

      <div>
        {fieldLabel('referral-contact-name', t('referral.contactLabel'))}
        <input
          id="referral-contact-name"
          value={value.contact_name}
          onChange={(e) => commit({ ...value, contact_name: e.target.value })}
          placeholder={t('referral.contactPlaceholder')}
          autoComplete="off"
          className={`${inputClass} mt-2`}
        />
      </div>
    </div>
  );
}
